import { useMemo } from 'react';
import { Product } from '@setvi/shared/interfaces';
import { SInfiniteTable } from '@setvi/shared/components/s-table/infinite';
import { useResourceOpener } from '@setvi/shared/providers';
import { SText, TableLoader } from '@setvi/shared/components';
import { Box } from '@material-ui/core';
import { useProductAssetsEnabled } from 'hooksv2';
import { useAssociatedResourceColumns } from './useAssociatedResourceColumns';
import { useAssociatedResource } from './useAssociatedResource';
import { ContentTitleContainer } from '../product-info';
import { ProductsEmptyState } from '../../components';
import { ProductAssets } from './product-assets';

export interface AssociatedResourceProps {
  product: Product;
  closeDialog?: () => void;
  captureResourceAnalytics?: (additionalValues?: {
    [key: string]: string;
  }) => void;
  readOnly?: boolean;
}

/** Setvi associated resources: the Setvi API, opened through the resource opener. */
const SetviAssociatedResource = ({
  product,
  readOnly,
  closeDialog,
  captureResourceAnalytics
}: AssociatedResourceProps) => {
  const { resources, isLoading } = useAssociatedResource({ product });
  const { openResource } = useResourceOpener();
  const { columns } = useAssociatedResourceColumns({
    closeDialog,
    readOnly
  });

  const content = useMemo(() => {
    if (isLoading) return <TableLoader columns={columns} />;

    if (!resources.length && !isLoading)
      return <ProductsEmptyState title="No Resources Available" />;

    return (
      <SInfiniteTable
        newDesign
        stickyHeader={false}
        list={resources}
        columns={columns}
        keyGetter={resource => resource?.ID}
        isLoading={isLoading}
        row={{
          onClick: resource => {
            captureResourceAnalytics?.({ categoryName: resource.CategoryName });
            openResource({
              name: resource.Name,
              resourceTypeId: resource.ResourceTypeID,
              resourceId: resource.ResourceID,
              resource
            });
          }
        }}
      />
    );
  }, [isLoading, columns, resources, captureResourceAnalytics, openResource]);

  return (
    <Box flex={1} display="flex" flexDirection="column" width="100%">
      <ContentTitleContainer mb={2}>
        <SText title="Resources" weight="semibold" />
      </ContentTitleContainer>
      {content}
    </Box>
  );
};

/**
 * The Resources tab. Product-assets companies (CFS, LD flag) read revops-api
 * product assets and open them in a new tab; everyone else keeps the Setvi
 * associated resources. Split so the disabled branch never fires its query.
 */
export const AssociatedResource = (props: AssociatedResourceProps) => {
  const productAssetsEnabled = useProductAssetsEnabled();

  if (productAssetsEnabled) return <ProductAssets product={props.product} />;

  return <SetviAssociatedResource {...props} />;
};
